import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { AdditionalFields } from '../../models/models';
import { defaultDoc, getValueFromLocalStorage } from '../share/defaultDoc';

export interface SignAndStampUserLevel {
  readonly fileId: string;
  readonly inspectionId: string;
  readonly fileUUID: string;
  readonly fileCategory: number;
  readonly drawingType: number;
  readonly isImageFile: boolean;
  readonly pcsId: string;
  readonly sizeMb: number;
  readonly originalPath: string;
  readonly extension: string;
  readonly name: string;
  readonly originalFileName: string;
  readonly allowSpaccess: boolean;
  readonly legacyId: number;
  readonly fkAssetInspectionTask: number;
  readonly siteId: number;
  readonly disciplineId: number;
  readonly fileDescription: string;
  readonly showOnReport: boolean;
  readonly isDelete: string;
  readonly isSaveForFuture: boolean;
  readonly userId: number;
  readonly dateCreated?: string;
  readonly dateModified?: string;
  readonly leadInspectorName?: string;
  readonly leadInspectorTitle?: string;
  readonly leadCompanyName?: string;
  readonly leadDesignation?: string;
  readonly fieldInspectorName?: string;
  readonly fieldInspectorTitle?: string;
  readonly fieldCompanyName?: string;
  readonly fieldDesignation?: string;
}

export const signAndStampUserLevelSchema: RxJsonSchema<SignAndStampUserLevel> = {
  type: 'object',
  version: 1,
  description: 'describes sign and stamp files',
  primaryKey: 'fileUUID',
  properties: {
    fileId: { type: ['string', 'null'] },
    inspectionId: { type: ['string', 'null'] },
    fileUUID: { type: 'string', maxLength: 36 },
    fileCategory: { type: ['number', 'null'] },
    drawingType: { type: ['number', 'null'] },
    isImageFile: { type: ['boolean', 'null'] },
    pcsId: { type: ['string', 'null'] },
    sizeMb: { type: ['number', 'null'] },
    originalPath: { type: ['string', 'null'] },
    extension: { type: ['string', 'null'] },
    name: { type: ['string', 'null'] },
    originalFileName: { type: ['string', 'null'] },
    allowSpaccess: { type: ['boolean', 'null'] },
    legacyId: { type: ['number', 'null'] },
    fkAssetInspectionTask: { type: ['number', 'null'] },
    siteId: { type: ['number', 'null'] },
    disciplineId: { type: ['number', 'null'] },
    fileDescription: { type: ['string', 'null'] },
    showOnReport: { type: ['boolean', 'null'] },
    isDelete: { type: ['string', 'null'] },
    isSaveForFuture: { type: ['boolean', 'null'] },
    userId: { type: ['number', 'null'] },
    dateCreated: { type: 'string' },
    dateModified: { type: ['string', 'null'] },
    leadInspectorName: { type: ['string', 'null'] },
    leadInspectorTitle: { type: ['string', 'null'] },
    leadDesignation: { type: ['string', 'null'] },
    leadCompanyName: { type: ['string', 'null'] },
    fieldInspectorName: { type: ['string', 'null'] },
    fieldInspectorTitle: { type: ['string', 'null'] },
    fieldDesignation: { type: ['string', 'null'] },
    fieldCompanyName: { type: ['string', 'null'] },
  },
};

export const signAndStampUserLevelMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: SignAndStampUserLevelDocument) => {
    const newDoc = {
      ...oldDoc,
    };
    delete newDoc.inspectorId;
    return newDoc;
  },
};

export type SignAndStampUserLevelCollection = RxCollection<SignAndStampUserLevel> | null;
export type SignAndStampUserLevelDocument = RxDocument<SignAndStampUserLevel>;

export const signAndStampUserLevelPullQueryBuilder = (document: SignAndStampUserLevelDocument) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }

  const query = print(gql`
    query signAndStampFilesOnUserLevel(
      $siteId: Int!
      $disciplineId: Int!
      $lastModified: DateTime!
    ) {
      signAndStampFilesOnUserLevel(
        siteId: $siteId
        disciplineId: $disciplineId
        lastModified: $lastModified
      ) {
        fileId
        fileUUID
        fileCategory
        drawingType
        isImageFile
        pcsId
        sizeMb
        originalPath
        extension
        name
        originalFileName
        allowSpaccess
        legacyId
        siteId
        disciplineId
        fileDescription
        showOnReport
        isSaveForFuture
        userId
        dateCreated
        dateModified
        isDelete
      }
    }
  `);
  return {
    query,
    variables: {
      siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))
        ?.selectedDisciplineId,
      lastModified: doc?.dateModified ? doc?.dateModified : doc?.dateCreated,
    },
  };
};

export const signAndStampUserLevelPushQueryBuilder = (doc: SignAndStampUserLevel[]) => {
  const document = doc[0];
  const query = print(gql`
    mutation userSignAndStampFileInfo(
      $MobileUserSignAndStampInfoInput: MobileUserSignAndStampInfoInput
    ) {
      userSignAndStampFileInfo(userFileRequest: $MobileUserSignAndStampInfoInput) {
        fileId
        uUID
        inspectionId
        result
      }
    }
  `);

  const variables = {
    MobileUserSignAndStampInfoInput: document,
  };

  return {
    query,
    variables,
  };
};

export const signAndStampUserLevelPushModifier = (doc: SignAndStampUserLevelDocument) => {
  const obj: SignAndStampUserLevelDocument & {
    isBulkOperation: boolean;
    isLiStamp: boolean;
  } & AdditionalFields = {
    ...doc,
    isBulkOperation: false,
    isLiStamp: doc?.inspectionId?.length && doc?.fkAssetInspectionTask === 0 ? true : false,
  };
  const blackListedProp = [
    '_attachments',
    '_rev',
    '_deleted',
    'dateCreated',
    'dateModified',
    '_meta',
    'isDeleted',
  ];
  blackListedProp.forEach(property => {
    obj[property as keyof AdditionalFields] = undefined;
  });
  return obj;
};

export default {};
